import { useEffect, useId, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { ModalFooter, ModalHeader, ModalInput, ModalTextarea } from '@/components/admin/editorial-modal';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import {
  Baby,
  Car,
  Hospital,
  MoreHorizontal,
  Pencil,
  Plus,
  ShoppingBag,
  Utensils,
} from 'lucide-react';
import { api } from '@/services/api';
import { useTranslation } from 'react-i18next';
import { UI_URGENCY_LABEL_KEY, categoryLabel } from '@/lib/task-display';
import { labelFor } from '@/i18n/labels';
import { cn, localizedApiErrorMessage } from '@/lib/utils';
import { FieldLabel, MonoLabel } from '@/components/editorial';
import { PendingDecision } from '@/components/editorial/PendingDecision';
import type { Task } from '@/types/api';
import { serverMessage } from '@/lib/serverMessage';

const CATEGORY_KEYS = ['meal', 'ride', 'childcare', 'errand', 'hospital_stay', 'other'] as const;
type CategoryKey = (typeof CATEGORY_KEYS)[number];

const CATEGORY_ICON: Record<CategoryKey, typeof Utensils> = {
  meal: Utensils,
  ride: Car,
  childcare: Baby,
  errand: ShoppingBag,
  hospital_stay: Hospital,
  other: MoreHorizontal,
};

type UrgencyChoice = 'flexible' | 'soon' | 'urgent';
const URGENCY_TO_BACKEND: Record<UrgencyChoice, 'low' | 'medium' | 'high'> = {
  flexible: 'low',
  soon: 'medium',
  urgent: 'high',
};
// The reverse map is lossy on purpose: `critical` reads as "urgent" and, if
// the parent saves, is written back as `high` — the parent's own vocabulary.
const URGENCY_FROM_BACKEND: Record<Task['urgency'], UrgencyChoice> = {
  low: 'flexible',
  medium: 'soon',
  high: 'urgent',
  critical: 'urgent',
};

interface RequestHelpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  defaultCategory?: CategoryKey;
  defaultTitle?: string;
  defaultDate?: Date;
  /**
   * Edit mode (declutter 2026-09-10, spec §4 C6 "full edit"): the row pencil
   * opens this same modal prefilled from the task and saves through
   * PATCH /api/tasks/:id. The create path is untouched when this is unset.
   */
  editing?: Task | null;
  /** Called after a successful edit; the page invalidates the list. */
  onSaved?: () => void;
}

export function RequestHelpModal({
  open,
  onOpenChange,
  onCreated,
  defaultCategory = 'meal',
  defaultTitle = '',
  defaultDate,
  editing = null,
  onSaved,
}: RequestHelpModalProps) {
  const { t, i18n } = useTranslation(['tasks', 'common']);
  const { toast } = useToast();
  const isEditing = editing !== null;
  const [category, setCategory] = useState<CategoryKey>(defaultCategory);
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(formatDateInput(defaultDate));
  const [time, setTime] = useState(formatTimeInput(defaultDate));
  const [urgency, setUrgency] = useState<UrgencyChoice>('soon');
  const [submitting, setSubmitting] = useState(false);
  // #2412: a missing required field is marked beside itself (aria-invalid,
  // an inline line it is described by, focus), never a toast over the
  // dialog head. At 390 px the date sits below the fold, so focus scrolls it in.
  const [errors, setErrors] = useState<{ title?: boolean; date?: boolean }>({});
  const titleRef = useRef<HTMLInputElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const titleErrorId = useId();
  const dateErrorId = useId();

  const reset = () => {
    setErrors({});
    if (editing) {
      const when = editing.date_time ? new Date(editing.date_time) : undefined;
      setCategory(editing.category);
      setTitle(editing.title);
      setDescription(editing.description ?? '');
      setDate(formatDateInput(when));
      // #2410: an all-day task has no time to show. Its stored instant is
      // the local start of the day, which would prefill as 12:00 AM.
      setTime(editing.all_day ? '' : formatTimeInput(when));
      setUrgency(URGENCY_FROM_BACKEND[editing.urgency] ?? 'soon');
      return;
    }
    setCategory(defaultCategory);
    setTitle(defaultTitle);
    setDescription('');
    setDate(formatDateInput(defaultDate));
    setTime(formatTimeInput(defaultDate));
    setUrgency('soon');
  };

  // Prefill when the pencil opens the modal on a task, or on another task.
  // The modal keeps local state, so this is the one place the task flows in.
  useEffect(() => {
    if (open && editing) reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, open]);

  const submit = async () => {
    const missing = { title: !title.trim(), date: !date };
    if (missing.title || missing.date) {
      setErrors(missing);
      const first = missing.title ? titleRef.current : dateRef.current;
      first?.focus();
      first?.scrollIntoView?.({ block: 'center' });
      return;
    }
    setSubmitting(true);
    try {
      // #2410 (D-2410): no time means an all-day task — the local start of
      // the chosen day plus all_day, never an invented 9:00 AM.
      const allDay = !time;
      const dt = new Date(`${date}T${allDay ? '00:00' : time}`);
      if (editing) {
        await api.tasks.update(editing._id, {
          title: title.trim(),
          description: description.trim(),
          category,
          date_time: dt.toISOString(),
          all_day: allDay,
          urgency: URGENCY_TO_BACKEND[urgency],
        });
        toast({ title: t('request.toast.updated'), description: t('request.toast.updatedBody') });
        onOpenChange(false);
        onSaved?.();
        return;
      }
      await api.tasks.create({
        title: title.trim(),
        description: description.trim() || undefined,
        category,
        date_time: dt.toISOString(),
        all_day: allDay,
        urgency: URGENCY_TO_BACKEND[urgency],
      });
      // #649: no notification machinery exists — the circle is a contact
      // list, not reachable accounts. Say what actually happened.
      toast({ title: t('request.toast.added'), description: t('request.toast.addedBody') });
      reset();
      onOpenChange(false);
      onCreated();
    } catch (e) {
      // R3-B11: an English reader keeps today's description (the error's
      // own message); every other language gets the localized generic line.
      // The reader's chosen language, not the resolved bundle (which reads
      // `en` while a target catalog is still loading or empty).
      const language = i18n.language;
      const isEnglish = language === 'en' || language.startsWith('en-');
      toast({
        variant: 'destructive',
        title: isEditing ? t('request.toast.saveFailed') : t('request.toast.addFailed'),
        description: isEnglish
          ? serverMessage(e, t('common:states.pleaseTryAgain'))
          : localizedApiErrorMessage(e, t, language),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-xl flex flex-col max-h-[90vh] p-0 gap-0">
        {isEditing ? (
          <ModalHeader icon={Pencil} eyebrow={t('request.editEyebrow')} title={t('request.editTitle')} />
        ) : (
          <ModalHeader icon={Plus} eyebrow={t('request.newEyebrow')} title={t('request.newTitle')} />
        )}

        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-5 min-h-0">
          <Field label={t('request.field.category')} group="request-category">
            <div className="grid grid-cols-3 gap-2">
              {CATEGORY_KEYS.map((key) => {
                const Icon = CATEGORY_ICON[key];
                const isActive = category === key;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setCategory(key)}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border px-3 py-2.5 text-body transition-colors text-left',
                      isActive
                        ? 'border-stevens-indigo bg-stevens-indigo/5 text-foreground'
                        : 'border-border hover:bg-muted',
                    )}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0 text-stevens-ink-3" />
                    <span>{categoryLabel(key, t)}</span>
                  </button>
                );
              })}
            </div>
          </Field>

          <Field
            label={t('request.field.what')}
            error={errors.title ? t('request.toast.titleMissingBody') : undefined}
            errorId={titleErrorId}
          >
            <ModalInput
              ref={titleRef}
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors((x) => ({ ...x, title: false }));
              }}
              placeholder={t('request.placeholder.what')}
              aria-required="true"
              aria-invalid={errors.title || undefined}
              aria-describedby={errors.title ? titleErrorId : undefined}
              className={errors.title ? 'border-destructive' : undefined}
              autoFocus
            />
          </Field>

          <Field label={t('request.field.details')}>
            <ModalTextarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('request.placeholder.details')}
              rows={3}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t('request.field.when')}
              error={errors.date ? t('request.toast.dateMissingBody') : undefined}
              errorId={dateErrorId}
            >
              <ModalInput
                ref={dateRef}
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  if (errors.date) setErrors((x) => ({ ...x, date: false }));
                }}
                aria-required="true"
                aria-invalid={errors.date || undefined}
                aria-describedby={errors.date ? dateErrorId : undefined}
                className={errors.date ? 'border-destructive' : undefined}
              />
            </Field>
            <Field label={t('request.field.time')}>
              <ModalInput
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </Field>
          </div>
          {/* #2410: clearing the time on a timed task makes it all-day — an
              unruled default (D-2410 rules the storage only). Outside the
              time field's <label>, so its name stays "Time". */}
          {isEditing && (
            <div className="-mt-3 flex justify-end">
              <PendingDecision id="task-clear-time-makes-all-day" side="top" />
            </div>
          )}
          {isEditing && editing?.recurrence_id && (
            <p className="text-caption text-stevens-ink-3 -mt-3">
              {t('request.occurrenceOnly')}
            </p>
          )}

          <Field label={t('request.field.urgency')} group="request-urgency">
            <div className="grid grid-cols-3 gap-2">
              {(['flexible', 'soon', 'urgent'] as UrgencyChoice[]).map((u) => {
                const isActive = urgency === u;
                return (
                  <button
                    key={u}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setUrgency(u)}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-body transition-colors',
                      isActive
                        ? 'border-stevens-indigo bg-stevens-indigo/5 text-foreground'
                        : 'border-border hover:bg-muted',
                    )}
                  >
                    {/* A label, not the raw enum under CSS capitalize (R3-B22). */}
                    {labelFor(UI_URGENCY_LABEL_KEY, u, t)}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        <ModalFooter
          lead={
            <MonoLabel className="text-[11px] font-normal">
              {isEditing ? t('request.footer.saved') : t('request.footer.goes')}
            </MonoLabel>
          }
        >
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            size="sm"
            variant="brand"
            onClick={submit}
            disabled={submitting}
          >
            {isEditing ? <Pencil className="h-3.5 w-3.5 mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
            {isEditing
              ? (submitting ? t('common:states.saving') : t('common:actions.saveChanges'))
              : (submitting ? t('request.adding') : t('request.add'))}
          </Button>
        </ModalFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A wrapping `<label>` for a single input; `group` for a row of chip buttons.
 * A `<label>` labels its FIRST labelable descendant, so wrapping a button
 * group in one handed the group's heading to its first chip as that chip's
 * accessible name ("Meal" read as "Category"). A named group is the right
 * association for one question answered by several buttons.
 */
function Field({
  label,
  group,
  error,
  errorId,
  children,
}: {
  label: string;
  group?: string;
  /** An inline error line, rendered OUTSIDE the label so it never joins the field's name. */
  error?: string;
  errorId?: string;
  children: React.ReactNode;
}) {
  if (group) {
    const id = `${group}-label`;
    return (
      <div role="group" aria-labelledby={id} className="block">
        <FieldLabel id={id} as="span" className="mb-1.5">{label}</FieldLabel>
        {children}
      </div>
    );
  }
  return (
    <div>
      <label className="block">
        <FieldLabel as="span" className="mb-1.5">{label}</FieldLabel>
        {children}
      </label>
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-caption text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function formatDateInput(d?: Date): string {
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatTimeInput(d?: Date): string {
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
